import $ from 'jquery';
import '../../vendor/d3';

const { d3 } = globalThis;

type Point = {
  date: Date;
  value: number;
};

type LineBase = {
  group: string;
  displayName: string;
  dataSeriesName: string;
  color_class: string;
};

type Line = LineBase & { returnsByDate: Point[] };
type ActivePoint = LineBase & { value: number };

type Options = { lines: Line[] };
type Settings = Options & {
  $el: JQuery;
  width: number;
  height: number;
};

type Ranges = {
  minReturn: number;
  maxReturn: number;
  uniqueDates: number[];
  minDate: Date;
  maxDate: Date;
};

export function renderReturnsGraph(element: HTMLElement, options: Options): void {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const graph = new ReturnsGraph($(element), options);
  graph.create();
}

const GRAPH_HEIGHT = 375;
const MARGIN_TOP = 10;
const MARGIN_RIGHT = 10;
const MARGIN_BOTTOM = 30;
const MARGIN_LEFT = 60;
const TOOLTIP_CLASS = '.ReturnsGraph-tooltip';
const TRANSITION_DURATION = 800;
const Y_TICKS = 4;

class ReturnsGraph {
  private settings: Settings;

  private ranges: Ranges;

  private scales: {
    x: d3.time.Scale<number, number>;
    y: d3.scale.Linear<number, number>;
  };

  private generators: {
    xAxis: d3.svg.Axis;
    yAxis: d3.svg.Axis;
    line: d3.svg.Line<Point>;
    zero: d3.svg.Line<Point>;
  };

  private svg: d3.Selection<HTMLElement>;

  constructor($el: JQuery, { lines }: Options) {
    const width = $el.width()! - MARGIN_LEFT - MARGIN_RIGHT;
    const height = Math.min(0.75 * width, GRAPH_HEIGHT) - MARGIN_TOP - MARGIN_BOTTOM;

    this.settings = { $el, width, height, lines };
  }

  private hasData(): boolean {
    return this.settings.lines.some(line => line.returnsByDate.length > 0);
  }

  public create(): void {
    this.setRangeValues();
    this.setUpScales();
    this.setUpGenerators();
    this.createLayout();
    this.drawLines();
    this.initHover();

    if (!this.hasData()) {
      this.showZeroState();
    }
  }

  private showZeroState(): void {
    this.settings.$el.parent().find('.ReturnsGraph-zeroState').addClass('u-displayFlex');
  }

  private setRangeValues(): void {
    this.ranges = this.settings.lines.reduce(
      (memo, line) => {
        line.returnsByDate.forEach(ret => {
          if (typeof memo.minReturn === 'undefined' || ret.value < memo.minReturn) {
            memo.minReturn = ret.value;
          }
          if (typeof memo.maxReturn === 'undefined' || ret.value > memo.maxReturn) {
            memo.maxReturn = ret.value;
          }
        });
        const lineDays = line.returnsByDate.map(ret => ret.date.getTime());
        memo.uniqueDates = Array.from(new Set([...lineDays, ...memo.uniqueDates]));
        return memo;
      },
      { uniqueDates: [] } as unknown as Ranges
    );

    this.ranges.uniqueDates.sort();
    this.ranges.minDate = new Date(this.ranges.uniqueDates[0]);
    this.ranges.maxDate = new Date(this.ranges.uniqueDates.at(-1)!);
  }

  private setUpScales(): void {
    const yDomainValues = d3.extent(yTickValues(this.ranges.minReturn, this.ranges.maxReturn, Y_TICKS));

    this.scales = {
      x: d3.time.scale().domain([this.ranges.minDate, this.ranges.maxDate]).range([0, this.settings.width]),
      y: d3.scale.linear().domain(yDomainValues).range([this.settings.height, 0]).nice(),
    };
  }

  private setUpGenerators(): void {
    const { scales } = this;
    this.generators = {
      xAxis: d3.svg
        .axis()
        .scale(scales.x)
        .tickSize(10, 0)
        .tickValues(this.scales.x.domain())
        .tickPadding(5)
        .orient('bottom')
        .tickFormat((date: Date) => d3.time.format('%b %e, %Y')(date)),
      yAxis: d3.svg
        .axis()
        .scale(scales.y)
        .tickSize(-1 * this.settings.width, 0)
        .tickValues(yTickValues(this.ranges.minReturn, this.ranges.maxReturn, Y_TICKS))
        .tickPadding(5)
        .tickFormat((d: number) => percentFormat(d))
        .orient('left'),
      line: d3.svg
        .line<Point>()
        .x(d => scales.x(d.date))
        .y(d => scales.y(d.value)),
      zero: d3.svg
        .line<Point>()
        .x(d => scales.x(d.date))
        .y(() => scales.y(0)),
    };
  }

  private createLayout(): void {
    this.svg = (d3.select(`#${this.settings.$el.attr('id')}`) as d3.Selection<HTMLElement>)
      .attr('width', this.settings.width + MARGIN_LEFT + MARGIN_RIGHT)
      .attr('height', this.settings.height + MARGIN_TOP + MARGIN_BOTTOM)
      .on('mousemove', () => this.updateHover())
      .on('mouseleave', () => this.hideHover())
      .append('g')
      .attr('transform', `translate(${MARGIN_LEFT},${MARGIN_TOP})`);

    this.svg
      .append('g')
      .attr('class', 'ReturnsGraph-axis--x ReturnsGraph-axis')
      .attr('transform', `translate(0,${this.settings.height})`)
      .call(this.generators.xAxis)
      .selectAll('text')
      .style('text-anchor', (_tick, idx) => (idx % 2 === 0 ? 'start' : 'end'));

    this.svg
      .append('g')
      .attr('class', 'ReturnsGraph-axis--y ReturnsGraph-axis')
      .attr('transform', 'translate(0, 0)')
      .call(this.generators.yAxis);

    this.svg.append('path').attr('class', 'ReturnsGraph-hoverable ReturnsGraph-bar');

    this.svg.selectAll('g.tick').attr('class', 'tick ReturnsGraph-axisTick ReturnsGraph-label');

    this.svg.selectAll('g .ReturnsGraph-axis--y .tick').style('stroke-dasharray', tickLine => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return parseInt(tickLine, 10) === 0 ? 'none' : '5';
    });
  }

  private drawLines(): void {
    this.settings.lines.forEach(singleLine => {
      this.appendLine(singleLine);
    });
    // make sure all of the svg circles are drawn after (on top of the paths)
    this.settings.lines.forEach(singleLine => {
      this.appendCircle(singleLine);
      this.updateLine(singleLine);
    });
  }

  private initHover(): void {
    this.updateHover(this.settings.width);
    this.hideHover();
  }

  private updateHoverBar(date: Date): void {
    d3.select(`#${this.settings.$el.attr('id')} .ReturnsGraph-hoverable.ReturnsGraph-bar`)
      .classed('ReturnsGraph-hoverable--show', true)
      .attr('d', `M${this.scales.x(date)},${this.settings.height}L${this.scales.x(date)},${0}`);
  }

  private updateCirclesTooltipValue(date: Date, activePoints: ActivePoint[]): void {
    $(TOOLTIP_CLASS).find('li').remove();
    d3.selectAll('.ReturnsGraph-hoverable.ReturnsGraph-circle').classed('ReturnsGraph-hoverable--show', false);
    activePoints.forEach(point => {
      const circle = d3.select(`[data-name="${point.dataSeriesName}"].ReturnsGraph-circle`);

      circle
        .attr('class', `ReturnsGraph-hoverable ReturnsGraph-hoverable--show ReturnsGraph-circle ${point.color_class}`)
        .attr('cx', this.scales.x(date))
        .attr('cy', this.scales.y(point.value));

      const legend = document.createElement('span');
      legend.classList.add('ReturnsGraph-tooltipLegend');
      legend.classList.add(point.color_class);

      const name = document.createElement('span');
      name.classList.add('ReturnsGraph-tooltipName');
      name.textContent = point.displayName;

      const value = document.createElement('span');
      value.classList.add('ReturnsGraph-tooltipValue');
      value.textContent = percentFormat(point.value);

      const li = document.createElement('li');
      li.replaceChildren(legend, name, value);

      $(TOOLTIP_CLASS).find('ul').append(li);
    });
  }

  private updateTooltip(options: { date: Date; offset: number; tooltipWidth: number }): void {
    $(TOOLTIP_CLASS).find('.ReturnsGraph-tooltipDate').text(dateFormat(options.date));
    $(TOOLTIP_CLASS)
      .css({
        left: this.scales.x(options.date) + MARGIN_LEFT - (options.offset > 0 ? options.tooltipWidth - 1 : 1),
      })
      .addClass('ReturnsGraph-hoverable--show');
  }

  private updateHover(mouseX?: number): void {
    if (typeof mouseX === 'undefined') {
      const container = d3.select(`#${this.settings.$el.attr('id')}`);
      mouseX = d3.mouse(container.node())[0] - MARGIN_LEFT;
    }

    if (mouseX >= 0 && mouseX <= this.settings.width) {
      const date = this.scales.x.invert(mouseX);
      const tooltipWidth = $(TOOLTIP_CLASS).outerWidth()!;
      const offset = mouseX - tooltipWidth;
      const activePoints = this.getActivePointsForDate(date);
      this.updateHoverBar(date);
      this.updateTooltip({ date, offset, tooltipWidth });
      this.updateCirclesTooltipValue(date, activePoints);
    } else {
      this.hideHover();
    }
  }

  private hideHover(): void {
    d3.selectAll('.ReturnsGraph-hoverable').classed('ReturnsGraph-hoverable--show', false);
  }

  private appendLine(line: Line): void {
    this.svg
      .append('path')
      .datum(line.returnsByDate)
      .attr('class', `ReturnsGraph-line ${line.group} ${line.color_class}`)
      .attr('data-name', line.dataSeriesName)
      .attr('d', this.generators.zero);
  }

  private updateLine(line: Line): void {
    d3.select(`[data-name="${line.dataSeriesName}"].ReturnsGraph-line`)
      .datum(line.returnsByDate)
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('class', `ReturnsGraph-line ${line.group} ${line.color_class}`)
      .attr('d', this.generators.line);
  }

  private appendCircle(line: Line): void {
    this.svg
      .append('circle')
      .attr('r', 3.5)
      .attr('data-name', line.dataSeriesName)
      .attr('class', `ReturnsGraph-hoverable ReturnsGraph-circle ${line.color_class}`);
  }

  private getActivePointsForDate(date: Date): ActivePoint[] {
    const bisect = d3.bisector(d => d).right;
    const time = date.getTime();
    const index = bisect(this.ranges.uniqueDates, time);
    const t0 = this.ranges.uniqueDates[index - 1];
    const t1 = this.ranges.uniqueDates[index];
    const closestDate = new Date(time - t0 > t1 - time ? t1 : t0);

    return this.settings.lines.reduce((activePoints: ActivePoint[], line) => {
      const match = line.returnsByDate.find(point => point.date.getTime() === closestDate.getTime());
      if (!match) return activePoints;

      return activePoints.concat({
        color_class: line.color_class,
        dataSeriesName: line.dataSeriesName,
        displayName: line.displayName,
        group: line.group,
        value: match.value,
      });
    }, []);
  }
}

function dateFormat(date: Date): string {
  return d3.time.format('%B %d, %Y')(date);
}

function percentFormat(value: number): string {
  if (Math.abs(value) < 1) {
    return d3.format('.1p')(value / 100);
  }
  return d3.format('.1%')(value / 100);
}

function yTickValues(minReturn: number, maxReturn: number, yTickCount: number): number[] {
  const buffer = Math.abs(maxReturn - minReturn) / 6;
  const range = maxReturn + buffer - (minReturn - buffer);
  const step = Math.ceil(range / yTickCount);
  const lowestPointInRange = Math.floor(minReturn / step) * step;

  const tickValues = [];
  for (let index = 0; index < yTickCount + 1; index += 1) {
    tickValues.push(index * step + lowestPointInRange);
  }

  return tickValues;
}
